import React, { forwardRef } from 'react';
import type { CheckboxRootState } from '@base-ui/react/checkbox';
import { Checkbox as BaseCheckbox } from '@base-ui/react/checkbox';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';
import cn from '~/Utils/cn';

import OptionalLabel from '../../internal/OptionalLabel';
import usePressRipple from '../../internal/usePressRipple';

type CheckboxSize = 'xs' | 'small' | 'medium';

export const SIZES = {
  xs: 'xs',
  small: 'small',
  medium: 'medium',
} as const satisfies Record<CheckboxSize, CheckboxSize>;

/**
 * The change event handlers receive: the hidden input's own native change event, typed by what it
 * truly is. Reading `target.checked` is the contract; synthetic-event members (`persist`,
 * `nativeEvent`, ...) do not exist on it, and this type makes reaching for them a compile error.
 */
export type CheckboxChangeEvent = BaseCheckbox.Root.ChangeEventDetails['event'] & { target: HTMLInputElement };

export interface CheckboxProps {
  /**
   * `undefined` on the first render mounts the checkbox uncontrolled, and the mode is latched: a
   * value that arrives later is ignored, with only a dev-mode warning. Pass a boolean from the
   * first render for a controlled checkbox.
   */
  checked?: boolean;
  /**
   * Renders the mixed state (`aria-checked="mixed"`, a dash glyph), e.g. a "select all" over a
   * partial selection. It wins over `checked` for display; a click still fires `onChange` with
   * the next value of `checked`.
   */
  indeterminate?: boolean;
  /**
   * Fired with the change event of the hidden `<input type="checkbox">`, whose `target.checked`
   * already holds the new value, so handlers may read either argument. `target` is a live
   * reference: read it inside the handler, before a controlled re-render resets the input.
   */
  onChange?: (event: CheckboxChangeEvent, checked: boolean) => void;
  onBlur?: (event: React.FocusEvent<HTMLElement>) => void;
  disabled?: boolean;
  className?: string;
  /** Applied to the hidden input, so an external `<label htmlFor>` keeps working. */
  id?: string;
  /** Identifies the field when a form is submitted. */
  name?: string;
  /** @default 'medium' */
  size?: CheckboxSize;
  /**
   * Declared because FormControlLabel (core/Atomic) clones its control with a `required` key on
   * every render — usually holding `undefined` — which the unexpected-props guard would otherwise
   * report. When actually set, it reaches the hidden input (native constraint semantics) and
   * `aria-required` on the checkbox.
   */
  required?: boolean;
  'aria-label'?: string;
  /**
   * A label rendered beside the checkbox, wired to it as `aria-labelledby`, so it also names the
   * checkbox for assistive technology and wins over `aria-label` when both are given. Clicking it
   * toggles. Use one labelling channel per checkbox: not alongside an external `<label htmlFor>`
   * or a FormControlLabel.
   */
  label?: React.ReactNode;
}

// The glyph is 1em square, so the root's font size sets it: 15px (xs), 20px (small) or 24px
// (medium). With the root's 9px padding, the round hit area is 33px, 38px or 42px. The root is
// the hit area, the layout footprint and the interaction halo. xs and medium keep the MUI
// geometry the app was built around; small fills the gap between them.
const glyphSizeClasses = {
  xs: 'text-[15px]',
  small: 'text-[20px]',
  medium: 'text-[24px]',
} as const satisfies Record<CheckboxSize, string>;

// The interaction halo follows MUI rather than Switch: a faint teal on hover in every state, and a
// stronger currentColor halo at 20% on keyboard focus, blended by the browser because a
// build-time opacity modifier cannot apply to `currentColor`.
const haloInteractionClasses =
  'hover:bg-teal-700/[0.06] focus-visible:bg-[color-mix(in_srgb,currentColor_20%,transparent)]';

// One 24-unit box, drawn as a single even-odd path, so the check mark and the dash are holes that
// show the background rather than a painted colour that could clash with it.
const BOX_PATH = 'M5 3h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z';
const CHECK_HOLE_PATH = 'M10 17l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8z';
const DASH_HOLE_PATH = 'M7 11h10v2H7z';

const Glyph = ({ state }: { state: CheckboxRootState }) => (
  <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden className="block fill-current">
    {state.indeterminate ? (
      <path fillRule="evenodd" d={`${BOX_PATH}${DASH_HOLE_PATH}`} />
    ) : state.checked ? (
      <path fillRule="evenodd" d={`${BOX_PATH}${CHECK_HOLE_PATH}`} />
    ) : (
      <rect x="4" y="4" width="16" height="16" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
    )}
  </svg>
);

const Checkbox = forwardRef<HTMLSpanElement, CheckboxProps>(
  (
    {
      checked,
      indeterminate,
      onChange,
      onBlur,
      disabled,
      className,
      id,
      name,
      size = 'medium',
      required,
      'aria-label': ariaLabel,
      label,
      ...restProps
    },
    ref
  ) => {
    const ripple = usePressRipple();

    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to Checkbox: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the CheckboxProps interface and to the component's signature.`,
        true
      );
    }

    const handleCheckedChange = (nextChecked: boolean, eventDetails: BaseCheckbox.Root.ChangeEventDetails) => {
      // One truthful narrowing: the event always originates from the hidden input, so its target
      // is that input. (Base UI types `event` loosely as Event.)
      onChange?.(eventDetails.event as CheckboxChangeEvent, nextChecked);
    };

    // Classes derive from Base UI's state callback rather than data-attribute variants, so the
    // disabled colour deterministically beats the checked one instead of resting on stylesheet
    // order. Unchecked is grey, checked and mixed are teal, every disabled state is a faint grey.
    const rootClassName = (state: CheckboxRootState) =>
      cn(
        ripple.rootClassName,
        'p-[9px]',
        'inline-flex shrink-0 select-none items-center justify-center rounded-full align-middle outline-none',
        'transition-[background-color] duration-150',
        glyphSizeClasses[size],
        state.checked || state.indeterminate ? 'text-teal-700' : 'text-slate-700',
        state.disabled ? 'pointer-events-none cursor-default text-slate-600' : 'cursor-pointer',
        !state.disabled && haloInteractionClasses,
        className
      );

    return (
      // The checkbox's own 9px padding already spaces the text.
      <OptionalLabel label={label} id={id} disabled={disabled} className="gap-0">
        <BaseCheckbox.Root
          data-fs-component="checkbox"
          ref={ref}
          checked={checked === undefined ? undefined : Boolean(checked)}
          indeterminate={indeterminate}
          onCheckedChange={handleCheckedChange}
          {...ripple.getRootHandlers({ onBlur })}
          disabled={disabled}
          required={required}
          id={id}
          name={name}
          aria-label={ariaLabel}
          className={rootClassName}
          render={(props, state) => (
            <span {...props}>
              <Glyph state={state} />
              {ripple.rippleContainer}
            </span>
          )}
        />
      </OptionalLabel>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
